import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert
} from '@mui/material';
import {
  History,
  ArrowBack
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { vitalsAPI } from '../../services/api';

function VitalsHistory() {
  const { admissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const patientInfo = location.state || {};

  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadVitalsHistory();
  }, [admissionId]);

  const loadVitalsHistory = async () => {
    try {
      const response = await vitalsAPI.getPatientVitals(admissionId);
      const data = response.data.data || response.data;
      setVitals(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load vitals history:', err);
      setError('Failed to load vitals history');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPainLabel = (painScale) => {
    if (painScale === null || painScale === undefined) return '-';
    if (painScale === 0) return '😊 No Pain';
    if (painScale >= 1 && painScale <= 3) return '😐 Mild';
    if (painScale >= 4 && painScale <= 6) return '😣 Moderate';
    if (painScale >= 7 && painScale <= 9) return '😖 Severe';
    if (painScale === 10) return '😱 Worst';
    return painScale;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card elevation={2}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={() => navigate('/nursing')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <History sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" component="h2" fontWeight="bold">
                Vitals History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Patient: {patientInfo.patientName} ({patientInfo.patientCode}) • Room: {patientInfo.roomNumber}
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {vitals.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <History sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No vitals recorded yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vitals will appear here once recorded
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Date & Time</strong></TableCell>
                    <TableCell><strong>BP</strong></TableCell>
                    <TableCell><strong>Temp (°F)</strong></TableCell>
                    <TableCell><strong>Pulse</strong></TableCell>
                    <TableCell><strong>RR</strong></TableCell>
                    <TableCell><strong>SpO2 (%)</strong></TableCell>
                    <TableCell><strong>RBS (mg/dL)</strong></TableCell>
                    <TableCell><strong>Insulin (U)</strong></TableCell>
                    <TableCell><strong>Weight (kg)</strong></TableCell>
                    <TableCell><strong>Height (cm)</strong></TableCell>
                    <TableCell><strong>BMI</strong></TableCell>
                    <TableCell><strong>Pain</strong></TableCell>
                    <TableCell><strong>Recorded By</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vitals.map((vital) => (
                    <TableRow key={vital.id} hover>
                      <TableCell>{formatDateTime(vital.recordedAt)}</TableCell>
                      <TableCell>{vital.bloodPressure || '-'}</TableCell>
                      <TableCell>{vital.temperature || '-'}</TableCell>
                      <TableCell>{vital.pulseRate || '-'}</TableCell>
                      <TableCell>{vital.respiratoryRate || '-'}</TableCell>
                      <TableCell>{vital.oxygenSaturation || '-'}</TableCell>
                      <TableCell>{vital.rbs || '-'}</TableCell>
                      <TableCell>{vital.insulinUnits || '-'}</TableCell>
                      <TableCell>{vital.weight || '-'}</TableCell>
                      <TableCell>{vital.height || '-'}</TableCell>
                      <TableCell>{vital.bmi || '-'}</TableCell>
                      <TableCell>
                        {vital.painScale !== null && vital.painScale !== undefined ? (
                          <Chip
                            label={getPainLabel(vital.painScale) + ' - ' + vital.painScale}
                            size="small"
                            color={
                              vital.painScale === 0 ? 'success' :
                              vital.painScale <= 3 ? 'info' :
                              vital.painScale <= 6 ? 'warning' :
                              'error'
                            }
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {vital.recordedByName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {vital.recordedByRole}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {vitals.length > 0 && vitals[0].notes && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Latest Notes:
              </Typography>
              <Typography variant="body2">
                {vitals[0].notes}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default VitalsHistory;